class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    // Elements
    this.cachedResults = new Map();
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.searchButton = this.querySelector('.search__button');
    this.form = this.querySelector('form');
    this.statusElement = this.querySelector('.predictive-search-status');

    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isOpen = false;

    // Collection dropdown
    this.collectionSelect = document.getElementById('searchCollection');
    this.collectionHandleInput = document.getElementById('collectionHandle');

    this.closeButton = document.querySelector('.search-modal__close-button');
    this.menuDrawer = document.querySelector('.menu-toggle-navigation-button');

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.form) this.form.addEventListener('submit', this.onFormSubmit.bind(this));

    if (this.input) {
      this.input.addEventListener('input', this.debounce(this.onChange.bind(this), 300));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
    }

    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.clear.bind(this));
    }

    if (this.collectionSelect) {
      this.collectionSelect.addEventListener('change', this.onCollectionChange.bind(this));
    }

    document.addEventListener('click', this.handleClickOutside.bind(this));

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        document.body.classList.remove('search-open');
        this.closeButton.classList.remove('is-active');
        if (this.menuDrawer && !this.menuDrawer.classList.contains('is-show')) {
          this.menuDrawer.classList.add('is-show');
        }
      });
    }

  }

  getQuery() {
    return this.input?.value.trim() || '';
  }

  onChange() {

    const newSearchTerm = this.getQuery();

     if (this.resetButton) {
        if (newSearchTerm.length) {
          this.resetButton.classList.remove('hidden');
          this.searchButton.classList.add('hidden');

          document.body.classList.add('search-open');
          this.closeButton?.classList.add('is-active');

          if (this.menuDrawer && !this.menuDrawer.classList.contains('is-show')) {
            this.menuDrawer.classList.add('is-show');
          }
        } else {
          this.resetButton.classList.add('hidden');
          this.searchButton.classList.remove('hidden');

          document.body.classList.remove('search-open');
          this.closeButton?.classList.remove('is-active');

          if (this.menuDrawer && !this.menuDrawer.classList.contains('is-show')) {
            this.menuDrawer.classList.add('is-show');
          }
        }
      }

    if (!newSearchTerm.length) {
      this.close(true);
      return;
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.clearResults();
    }

    this.searchTerm = newSearchTerm;
    this.getSearchResults(this.searchTerm);
  }


  onCollectionChange() {
    if (this.collectionHandleInput && this.collectionSelect) {
      const val = this.collectionSelect.value;
      this.collectionHandleInput.value = val === 'all' ? '' : val;
    }
    if (this.searchTerm) {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const term = this.getQuery();

    if (!term.length) return;

    if (this.searchTerm !== term) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
      this.switchOption(event.code === 'ArrowUp' ? 'up' : 'down');
    } else if (event.code === 'Enter') {
      this.selectOption();
    } else if (event.code === 'Escape') {
      this.close(true);
    }
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.close(true);

    if (this.resetButton) {
      this.resetButton.classList.add('hidden');
      this.searchButton.classList.remove('hidden');
      this.closeButton.classList.remove('is-active');
    }
  }

  clearResults() {
    this.predictiveSearchResults.innerHTML = '';
  }

  getSearchResults(searchTerm) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.setLiveRegionText('Loading…');

    const collectionHandle = this.collectionSelect ?
      (this.collectionSelect.value === 'all' ? '' : this.collectionSelect.value) : '';

    const queryKey = `${searchTerm}-${collectionHandle}`.toLowerCase();

    if (this.cachedResults.has(queryKey)) {
      this.renderSearchResults(this.cachedResults.get(queryKey));
      return;
    }

    const baseUrl = `${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`;
    let url = baseUrl;
    if (collectionHandle) {
      url += `&collection=${collectionHandle}`;
    }

    fetch(url, { signal: this.abortController.signal })
      .then(r => {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(text => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const resultsWrapper = doc.querySelector('#shopify-section-predictive-search');
        if (resultsWrapper && resultsWrapper.innerText.trim() !== '') {
          const html = resultsWrapper.innerHTML;
          this.cachedResults.set(queryKey, html);
          this.renderSearchResults(html);
        } else if (collectionHandle) {
          // fallback to global search
          fetch(baseUrl, { signal: this.abortController.signal })
            .then(r => r.text())
            .then(txt => {
              const d = new DOMParser().parseFromString(txt, 'text/html');
              const rw = d.querySelector('#shopify-section-predictive-search');
              if (rw) this.renderSearchResults(rw.innerHTML);
              else this.close();
            });
        } else {
          this.close();
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') this.close();
      });
  }

  renderSearchResults(markup) {
    this.predictiveSearchResults.innerHTML = markup;
    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const count = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (count) this.setLiveRegionText(count.textContent);
  }

  setLiveRegionText(text) {
    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = text;
      setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selected = this.querySelector('[aria-selected="true"]');
    const all = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      el => el.offsetParent !== null
    );

    if (moveUp && !selected) return;
    let idx = all.indexOf(selected);
    let newIdx = moveUp
      ? (idx <= 0 ? all.length - 1 : idx - 1)
      : (idx === all.length - 1 ? 0 : idx + 1);

    const newEl = all[newIdx];
    newEl.setAttribute('aria-selected', 'true');
    if (selected) selected.setAttribute('aria-selected', 'false');
    this.input.setAttribute('aria-activedescendant', newEl.id);
  }

  selectOption() {
    const option = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (option) option.click();
  }

  open() {
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
    const maxHeight = window.innerHeight - headerBottom;
    this.predictiveSearchResults.style.maxHeight = `${maxHeight}px`;
    document.body.classList.add('search-open');
    this.closeButton?.classList.add('is-active');
  }

  close(clear = false) {
    if (clear) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
    document.body.classList.remove('search-open');
    this.closeButton?.classList.remove('is-active');
  }

  handleClickOutside(e) {
    if (!this.contains(e.target) && this.isOpen) this.close();
  }


  debounce(fn, delay) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);
